import { useMemo } from 'react';
import { Row } from '@tanstack/react-table';
import { DataTable, type DataTableColumnDef } from 'components/data-table';
import { Section } from 'components/section';
import type { AfeDetailCostItem, AfeDetailTotals } from '../types';
import styles from './cost-details-table.module.scss';

// ─────────────────────────────────────
// Display label mapping for cost categories
// ─────────────────────────────────────

const CATEGORY_DISPLAY_NAMES: Record<string, string> = {
  'Drilling': 'Drilling Costs',
  'Completion': 'Completion Costs',
  'Facilities': 'Facilities Costs',
  'Contingencies & Overhead': 'Contingencies & Overhead Costs',
};

/** Ordered list of categories matching the mock layout. */
const CATEGORY_ORDER = [
  'Drilling',
  'Completion',
  'Facilities',
  'Contingencies & Overhead',
];

const categoryDisplayName = (category: string): string =>
  CATEGORY_DISPLAY_NAMES[category] ?? `${category} Costs`;

// ─────────────────────────────────────
// Row Types
// ─────────────────────────────────────

interface CostCategoryRow extends Record<string, unknown> {
  rowType: 'category' | 'total';
  categoryKey: string;
  costItems?: AfeDetailCostItem[];
  categories: string;
  subCategory: string | null;
  nameOfCost: string | null;
  cost: number;
}

interface CostItemRow extends Record<string, unknown> {
  id: string;
  subCategory: string | null;
  nameOfCost: string;
  cost: number;
}

// ─────────────────────────────────────
// Data Transformation
// ─────────────────────────────────────

/**
 * Groups flat cost items into expandable category rows + a TOTAL row.
 * @param {AfeDetailCostItem[]} lineItems - Flat list of cost items.
 * @param {AfeDetailTotals} totals - Pre-calculated cost totals.
 * @return {CostCategoryRow[]} Category rows with a trailing TOTAL row.
 */
const buildCostRows = (
  lineItems: AfeDetailCostItem[],
  totals: AfeDetailTotals
): CostCategoryRow[] => {
  const grouped = new Map<string, AfeDetailCostItem[]>();

  for (const item of lineItems) {
    const key = item.category;
    const items = grouped.get(key) ?? [];
    items.push(item);
    grouped.set(key, items);
  }

  const rows: CostCategoryRow[] = [];

  // Ordered known categories first, then any extras
  const orderedKeys = [
    ...CATEGORY_ORDER.filter((k) => grouped.has(k)),
    ...[...grouped.keys()].filter((k) => !CATEGORY_ORDER.includes(k)),
  ];

  for (const category of orderedKeys) {
    const items = grouped.get(category) ?? [];
    const subtotal = items.reduce((sum, item) => sum + item.lineTotal, 0);

    rows.push({
      rowType: 'category',
      categoryKey: category,
      costItems: items,
      categories: categoryDisplayName(category),
      subCategory: null,
      nameOfCost: null,
      cost: subtotal,
    });
  }

  // Also show known categories that have zero cost (from totals) if no line items exist
  // This ensures the table always shows the standard categories

  rows.push({
    rowType: 'total',
    categoryKey: 'total',
    categories: 'TOTAL',
    subCategory: null,
    nameOfCost: null,
    cost: totals.totalAfeCost,
  });

  return rows;
};

// ─────────────────────────────────────
// Column Definitions (parent table)
// ─────────────────────────────────────

const parentColumns: DataTableColumnDef<CostCategoryRow>[] = [
  {
    id: 'categories',
    accessorKey: 'categories',
    header: 'Categories',
    meta: { type: 'text' },
  },
  {
    id: 'subCategory',
    accessorKey: 'subCategory',
    header: 'Sub-Category',
    meta: { type: 'text' },
    // Parent rows only show category name + cost; sub-category renders in expanded child rows
    cell: () => null,
  },
  {
    id: 'nameOfCost',
    accessorKey: 'nameOfCost',
    header: 'Name of Cost',
    meta: { type: 'text' },
    // Parent rows only show category name + cost; name renders in expanded child rows
    cell: () => null,
  },
  {
    id: 'cost',
    accessorKey: 'cost',
    header: 'Cost',
    meta: { type: 'currency' },
  },
];

// ─────────────────────────────────────
// Column Definitions (expanded sub-table)
// ─────────────────────────────────────

const childColumns: DataTableColumnDef<CostItemRow>[] = [
  {
    // Spacer standing in for the parent table's auto-added expand-toggle column.
    // Uses the id `_expander` so the shared column-width hook treats it as a
    // fixed 48px column (same as the parent), instead of distributing extra
    // width into it — which is what keeps the child columns aligned with the
    // parent headers regardless of viewport width.
    id: '_expander',
    header: '',
    size: 48,
    minSize: 48,
    maxSize: 48,
    enableSorting: false,
    enableResizing: false,
    meta: { isExportable: false },
    cell: () => null,
  },
  {
    // Mirrors the parent `categories` column so both table instances size it
    // identically; renders empty here since the category shows on the parent row.
    id: 'categories',
    accessorKey: 'categories',
    header: 'Categories',
    meta: { type: 'text' },
    cell: () => null,
  },
  {
    id: 'subCategory',
    accessorKey: 'subCategory',
    header: 'Sub-Category',
    meta: { type: 'text' },
  },
  {
    id: 'nameOfCost',
    accessorKey: 'nameOfCost',
    header: 'Name of Cost',
    meta: { type: 'text' },
  },
  {
    id: 'cost',
    accessorKey: 'cost',
    header: 'Cost',
    meta: { type: 'currency' },
  },
];

// ─────────────────────────────────────
// Sub-table for expanded rows
// ─────────────────────────────────────

const CostItemSubTable = ({ items }: { items: AfeDetailCostItem[] }) => {
  const rows: CostItemRow[] = items.map((item) => ({
    id: String(item.id),
    subCategory: item.subCategory,
    nameOfCost: item.name,
    cost: item.lineTotal,
  }));

  return (
    <DataTable<CostItemRow>
      data={rows}
      columns={childColumns}
      getRowId={(row) => row.id}
      config={{
        sorting: false,
        columnResizing: false,
        columnReordering: false,
        columnPinning: false,
        columnVisibility: false,
        globalSearch: false,
        columnFilters: false,
        export: false,
        hideHeader: true,
        hideFooter: true,
      }}
    />
  );
};

const renderRowSubComponent = (row: Row<CostCategoryRow>): React.ReactNode => {
  const { costItems } = row.original;
  if (!costItems || costItems.length === 0) return null;

  return <CostItemSubTable items={costItems} />;
};

// ─────────────────────────────────────
// Main Component
// ─────────────────────────────────────

interface CostDetailsTableProps {
  lineItems: AfeDetailCostItem[];
  totals: AfeDetailTotals;
}

const CostDetailsTable = ({ lineItems, totals }: CostDetailsTableProps) => {
  const data = useMemo(
    () => buildCostRows(lineItems, totals),
    [lineItems, totals]
  );

  return (
    <Section header="Costs Details">
      <div className={styles.tableContainer}>
        <DataTable<CostCategoryRow>
          data={data}
          columns={parentColumns}
          getRowId={(row) => `${row.rowType}-${row.categoryKey}`}
          renderRowSubComponent={renderRowSubComponent}
          getRowCanExpand={(row) => row.original.rowType === 'category'}
          config={{
            sorting: false,
            columnResizing: true,
            columnReordering: false,
            columnPinning: false,
            columnVisibility: true,
            globalSearch: true,
            columnFilters: false,
            export: true,
          }}
        />
      </div>
    </Section>
  );
};

export { CostDetailsTable };
